import {Injectable} from '@angular/core';
import {select, Store} from '@ngrx/store';
import {
  AppState,
  Category,
  ContractType,
  FilterState,
  Range,
  RegionID,
  SearchSubscriptionUserData,
  Subcategory,
} from '@immosky/types';
import {
  MINIMUM_RADIUS,
  RegionSearchService,
  selectFilterState,
} from '@immosky/object-search-filter';
import {NewsletterSignupService} from './newsletter-signup.service';
import {HttpClient} from '@angular/common/http';
import {I18nService} from '../i18n/i18n.service';

export interface SkynetSearchSubscription {
  FirstName: string;
  LastName: string;
  Street?: string;
  ZipCode?: string;
  City?: string;
  PhoneNumber?: string;
  MobileNumber?: string;
  Email: string;
  SearchTitle?: string;
  SearchIsBuy: boolean;
  SearchPriceFrom?: number;
  SearchPriceTo?: number;
  SearchRoomFrom?: number;
  SearchRoomTo?: number;
  SearchSpaceLivingFrom?: number;
  SearchSpaceLivingTo?: number;
  SearchSpacePropertyFrom?: number;
  SearchSpacePropertyTo?: number;
  SearchCategory?: number[];
  SearchSubCategory?: number[];
  SearchTerm?: string;
  SearchRadius: number;
  PostalCodes?: string[];
  CountryCode?: number;
  Language?: string;
}

export interface SearchSubscriptionRequest {
  userData: SearchSubscriptionUserData;
  filters: {
    contractType: ContractType;
    regionIDs: RegionID[];
    radius: number | null;
    category: {
      main: Category;
      sub: Subcategory[];
    };
    price: Range<number | null>;
    roomCount: Range<number | null>;
    livingSpace: Range<number | null>;
    usageSpace: Range<number | null>;
    propertySpace: Range<number | null>;
  };
}

const categoryMapping: {[cat in Category]: number} = {
  all: 0,
  house: 1,
  flat: 2,
  land: 3,
  income: 4,
  commercial: 6,
};
const subCategoryMap: {
  [subCat in Subcategory]: number | {commercial: number; income: number;};
} = {
  'one-family-house': 1,
  'mid-terrace-house': 2,
  'end-terrace-house': 3,
  'attic-flat': 4,
  'roof-flat': 5,
  rustico: 6,
  'building-land': 7,
  'agricultural-land': 9,
  'industrial-land': 10,
  maisonette: 11,
  studio: 12,
  'terrace-flat': 13,
  loft: 14,
  'semidetached-house': 15,
  'terrace-house': 17,
  farmhouse: 18,
  'business-house': {
    commercial: 19,
    income: 50,
  },
  mansion: 24,
  'multi-family-house': 25,
  'holiday-house': 28,
  'commercial-residential-building': {
    commercial: 29,
    income: 49,
  },
  office: 30,
  shop: 31,
  warehouse: 32,
  factory: 34,
  hotel: 35,
  restaurant: 36,
  'country-house': 45,
  'garden-flat': 46,
  'floor-flat': 47,
  'holiday-flat': 48,
  'two-family-house': 59,
  chalet: 66,
  practice: 71,
  'furnished-flat': 77,
  // 'commercial-residential-building': 49,
  // 'business-house': 50,
};

@Injectable({
  providedIn: 'root',
})
export class SearchSubscriptionService {
  filterState!: FilterState;

  request: Partial<SearchSubscriptionRequest> = {};

  isUserDataValid = false;

  get isValid(): boolean {
    return this.isUserDataValid;
  }

  constructor(
    private store: Store<AppState>,
    private regionSearchService: RegionSearchService,
    private newsletterSignupService: NewsletterSignupService,
    private i18nService: I18nService,
    private httpClient: HttpClient
  ) {
    this.store
      .pipe(select(selectFilterState))
      .subscribe((filterState: FilterState) => {
        this.filterState = filterState;
        this.updateFilters();
      });
  }

  private submitSearchSubscriptionToSkynet(data: SkynetSearchSubscription) {
    this.httpClient
      .post(`https://api.skynet.immosky.ch/search_agent`, data)
      .subscribe(() => {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const trackingService = window.trackingService;
        trackingService?.track('search_subscription_form_submission');
      });
  }

  submitSearchSubscription(country: string) {
    if (this.request.userData) {
      let country_code;
      if (country === 'CH') {
        country_code = 41;
      } else if (country === 'FR') {
        country_code = 33;
      }

      let language: string = this.i18nService.currentLanguage;
      if (language === 'de' && country_code === 41) {
        language = 'ch-de';
      }
      if (language === 'fr' && country_code === 41) {
        language = 'ch-fr';
      }
      if (language === 'en' && country_code === 33) {
        language = 'fr-en';
      }

      const data: SkynetSearchSubscription = {
        FirstName: this.request.userData.firstName,
        LastName: this.request.userData.lastName,
        Email: this.request.userData.email,
        SearchIsBuy: this.request.filters?.contractType === 'buy',
        SearchPriceFrom: this.request.filters?.price.min || undefined,
        SearchPriceTo: this.request.filters?.price.max || undefined,
        SearchRoomFrom:
          this.request.filters?.category.main === 'land'
            ? undefined
            : this.request.filters?.roomCount.min || undefined,
        SearchRoomTo:
          this.request.filters?.category.main === 'land'
            ? undefined
            : this.request.filters?.roomCount.max || undefined,
        SearchSpaceLivingFrom:
          this.request.filters?.livingSpace.min || undefined,
        SearchSpaceLivingTo: this.request.filters?.livingSpace.max || undefined,
        SearchSpacePropertyFrom:
          this.request.filters?.propertySpace.min || undefined,
        SearchSpacePropertyTo:
          this.request.filters?.propertySpace.max || undefined,
        SearchCategory: this.getCategory(),
        SearchRadius: this.getSearchRadius(),
        SearchSubCategory: this.getSubcategory(),
        CountryCode: country_code,
        Language: language,
      };

      if (this.request.filters?.regionIDs) {
        this.regionSearchService
          .containedPostalCodesForIds(this.request.filters.regionIDs)
          .subscribe((containedPostalCodes) => {
            this.submitSearchSubscriptionToSkynet({
              ...data,
              PostalCodes: containedPostalCodes,
            });
          });
      } else {
        this.submitSearchSubscriptionToSkynet(data);
      }
      if (this.request.userData?.subscribeToNewsletter) {
        this.newsletterSignupService.signUp({
          first_name: this.request.userData?.firstName,
          last_name: this.request.userData?.lastName,
          email: this.request.userData?.email,
        });
      }
    }
  }

  private getCategory(): number[] | undefined {
    const category: Category | undefined = this.request.filters?.category.main;

    if (category && category !== 'all') {
      return [categoryMapping[category]];
    } else {
      return undefined;
    }
  }

  private getSubcategory(): number[] {
    return (this.request.filters?.category.sub || [])
      .map((x) => {
        if (typeof subCategoryMap[x] === 'object') {
          if (
            this.request.filters?.category.main === 'commercial' ||
            this.request.filters?.category.main === 'income'
          ) {
            return (
              subCategoryMap[x] as {commercial: number; income: number;}
            )[this.request.filters?.category.main];
          } else {
            return -1;
          }
        } else {
          return subCategoryMap[x] as number;
        }
      })
      .filter((x) => x > 0);
  }

  private getSearchRadius(): number {
    const radius = this.request.filters?.radius || 0;
    if (radius > MINIMUM_RADIUS) {
      return radius;
    }
    return MINIMUM_RADIUS;
  }

  setUserData(userData: SearchSubscriptionUserData) {
    this.request.userData = userData;
  }

  setIsUserDataValid(isValid: boolean) {
    this.isUserDataValid = isValid;
  }

  private updateFilters() {
    const contractType = this.filterState.contractType.filter.value;
    const regionIDs = this.filterState.region.regions.map((r) => r.id);

    const radius =
      regionIDs.length > 1 ? null : this.filterState.radius.filter.value;

    const mainCategory = this.filterState.category.main.filter.value;
    const subcategories = this.filterState.category.sub.filter[mainCategory]
      .filter((s) => s.isChecked)
      .map((s) => s.value);

    const category = {
      main: mainCategory,
      sub: subcategories,
    };

    const price = {
      min: this.filterState.price.filter.min.value,
      max: this.filterState.price.filter.max.value,
    };

    const roomCount = {
      min: this.filterState.roomCount.filter.min.value,
      max: this.filterState.roomCount.filter.max.value,
    };

    const livingSpace = {
      min: this.filterState.livingSpace.filter.min.value,
      max: this.filterState.livingSpace.filter.max.value,
    };

    const usageSpace = {
      min: this.filterState.usageSpace.filter.min.value,
      max: this.filterState.usageSpace.filter.max.value,
    };

    const propertySpace = {
      min: this.filterState.propertySpace.filter.min.value,
      max: this.filterState.propertySpace.filter.max.value,
    };

    this.request.filters = {
      contractType,
      regionIDs,
      radius,
      category,
      price,
      roomCount,
      livingSpace,
      usageSpace,
      propertySpace,
    };
  }
}
